import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  ReactNode,
} from "react"

type CartContextType = {
  isOpen: boolean
  addedAnimationKey: number
  openCart: () => void
  closeCart: () => void
  showAddedToCart: () => void
}

const CartContext = createContext<CartContextType | undefined>(undefined)
const CART_DRAWER_OPEN_DELAY_MS = 880

// eslint-disable-next-line react-refresh/only-export-components
export const useCartDrawer = () => {
  const context = useContext(CartContext)
  if (!context) {
    throw new Error("useCartDrawer must be used within CartProvider")
  }
  return context
}

export const CartProvider = ({ children }: { children: ReactNode }) => {
  const [isOpen, setIsOpen] = useState(false)
  const [addedAnimationKey, setAddedAnimationKey] = useState(0)
  const openDelayRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const clearOpenDelay = useCallback(() => {
    if (openDelayRef.current) {
      clearTimeout(openDelayRef.current)
      openDelayRef.current = null
    }
  }, [])

  const openCart = useCallback(() => {
    clearOpenDelay()
    setIsOpen(true)
  }, [clearOpenDelay])

  const closeCart = useCallback(() => {
    clearOpenDelay()
    setIsOpen(false)
  }, [clearOpenDelay])

  const showAddedToCart = useCallback(() => {
    setAddedAnimationKey((current) => current + 1)
    clearOpenDelay()

    if (isOpen) {
      return
    }

    openDelayRef.current = setTimeout(() => {
      setIsOpen(true)
      openDelayRef.current = null
    }, CART_DRAWER_OPEN_DELAY_MS)
  }, [clearOpenDelay, isOpen])

  useEffect(() => clearOpenDelay, [clearOpenDelay])

  const value = useMemo(
    () => ({
      isOpen,
      addedAnimationKey,
      openCart,
      closeCart,
      showAddedToCart,
    }),
    [addedAnimationKey, closeCart, isOpen, openCart, showAddedToCart]
  )

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  )
}
